/**
 * Voxium Script — referral (`via`) capture and CTA augmentation.
 *
 * The capture below runs at MODULE EVALUATION TIME (a plain top-level
 * call, not inside a React effect) because React's first paint happens
 * synchronously and every CTA reads getStoredRef() during that very
 * first render. A useEffect-based capture fires AFTER that first paint,
 * so on a fresh visit every CTA would bake in a `via`-less URL before
 * the effect ever runs. Since this module is imported before App's
 * component tree renders (App.js imports it for its side effect, and
 * every CTA file imports it for appUrl()), the capture below is
 * guaranteed to have already run by the time any component's JSX body
 * evaluates.
 *
 * Why this isn't a pure document.cookie implementation: Safari's ITP
 * caps the lifetime of cookies written via script (`document.cookie =`)
 * at ~7 days, which defeats a 90-day attribution window — and referral
 * links are frequently opened from QR codes / in-app browsers, which
 * are disproportionately Safari/WebKit. A cookie that arrives on a
 * same-origin HTTP response (a real `Set-Cookie` header) is not subject
 * to that cap. So the actual cookie write happens server-side, in
 * GET /api/ref (see backend/server.py) — triggered here by a fetch.
 * That fetch is async and fire-and-forget; it never blocks the
 * synchronous localStorage write below, which is what the very first
 * paint's CTA hrefs actually depend on via the `via=` safety net link
 * param (for visitors whose cookies never reach work.voxiumscript.com,
 * or who block cookies outright).
 *
 * `via` is never used for pricing, copy, or discounts — it is only ever
 * read back to (a) set itself again and (b) append to outbound CTA
 * URLs. Invalid input is dropped silently: no error, no page break.
 */

const VIA_RE = /^[a-z0-9-]{2,32}$/;

const COOKIE_NAME = "vox_ref";
const LS_REF = "vox_ref";
const LS_FIRST_VIA = "vox_first_via";
const LS_FIRST_TOUCH_TS = "vox_first_touch_ts";
const LS_LANDING_PATH = "vox_landing_path";

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const lsGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // private browsing / storage disabled — never throw
  }
};

const lsSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore — localStorage is strictly a best-effort safety net here
  }
};

const capture = () => {
  if (typeof window === "undefined") return; // guards non-browser module evaluation (tests, SSR)

  let raw = "";
  try {
    raw = new URLSearchParams(window.location.search).get("via") || "";
  } catch {
    return;
  }
  // Normalize before validating: "Jephte" -> "jephte". Anything still
  // invalid after lowercasing is ignored silently.
  const via = raw.toLowerCase();
  if (!VIA_RE.test(via)) return;

  // First-touch bundle: written once, never overwritten by a later
  // visit with a different `via`.
  if (!lsGet(LS_FIRST_VIA)) {
    lsSet(LS_FIRST_VIA, via);
    lsSet(LS_FIRST_TOUCH_TS, new Date().toISOString());
    lsSet(LS_LANDING_PATH, window.location.pathname);
  }
  // Latest valid `via` always wins — this is the value CTA links use.
  // Written synchronously, before React's first paint.
  lsSet(LS_REF, via);

  const path = encodeURIComponent(window.location.pathname);
  fetch(`/api/ref?via=${encodeURIComponent(via)}&path=${path}`, {
    credentials: "include",
  }).catch(() => {
    // Network hiccup or /api unreachable — the localStorage write above
    // already happened, so CTA links are still correct. Never break
    // the page over this.
  });
};

capture();

/** The current stored referral code (latest valid `via` wins), preferring
 * the real server-set cookie and falling back to the localStorage safety
 * net (used when cookies are blocked). Returns null if neither is present. */
export const getStoredRef = () => readCookie(COOKIE_NAME) || lsGet(LS_REF) || null;

/**
 * Appends the stored `via` (if any) and `lang` (if passed) to a CTA's
 * target URL — the safety net for visitors whose cookies never reach
 * work.voxiumscript.com. Reads fresh on every call, so a CTA re-rendered
 * after in-SPA navigation (e.g. the locale picker) always reflects the
 * current stored value. Returns `base` completely unchanged when there
 * is nothing to append.
 */
export const appUrl = (base, { lang } = {}) => {
  const ref = getStoredRef();
  if (!ref && !lang) return base;
  const params = new URLSearchParams();
  if (ref) params.set("via", ref);
  if (lang) params.set("lang", lang);
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}${params.toString()}`;
};
